import {Component, ElementRef, Inject, OnDestroy, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {ActionsSubject, Store} from '@ngrx/store';
import {ChapterInterface} from '@shared/interfaces/chapter.interface';
import {LibraryActions} from '@store/library';
import {ActivatedRoute} from '@angular/router';
import {TableOfContentsInterface} from '@shared/interfaces/table-of-contents.interface';
import {FileInterface} from '@core/services/source/source.service';
import {ExtensionHelper} from '@shared/helpers/extension-helper.service';
import {DocumentInterface} from '@shared/interfaces/document.interface';
import {SelectionActions, SelectionSelectors} from '@store/selection';
import {AddSourceDialogForm} from '@feature/sources/add-source-dialog/add-source-dialog.form';
import {ofType} from '@ngrx/effects';
import {Subscription} from 'rxjs';
import {SelectedFileInterface} from '../interface/select-file.interface';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';

@Component({
    selector: 'app-add-source-dialog',
    templateUrl: './add-source-dialog.component.html',
    styleUrls: ['./add-source-dialog.component.scss'],
})
export class AddSourceDialogComponent implements OnDestroy {
    #subscriptions: Subscription[] = [];

    readonly bookUuid: string;

    readonly buttonType = ButtonTypeEnum;

    @ViewChild('file', {static: false})
    file!: ElementRef;

    selectedFile?: SelectedFileInterface;

    isLoading: boolean = false;

    sourceForm: AddSourceDialogForm = new AddSourceDialogForm();

    chapters?: ChapterInterface[];

    chapterUuid?: string;

    paragraphs?: TableOfContentsInterface[];

    teacherOnlyActive: boolean = false;

    licenseUuidActive: boolean = false;

    isMobile: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: any,
        private dialogRef: MatDialogRef<AddSourceDialogComponent>,
        public extensionHelper: ExtensionHelper,
        public activatedRoute: ActivatedRoute,
        public store: Store,
        actionsSubject: ActionsSubject,
        breakpointObserver: BreakpointObserver,
    ) {
        const bookUuid = this.bookUuid = this.dialogData.bookUuid;

        this.#subscriptions.push(
            actionsSubject.pipe(ofType(LibraryActions.addSourceFileSuccess)).subscribe(data => this.#handleSourceFileSuccess(data.file)),
            actionsSubject.pipe(ofType(LibraryActions.addSourceSuccess)).subscribe(() => this.#handleAddSourceSuccess()),
            this.store.select(SelectionSelectors.selectChapter).subscribe(chapter => this.#handleSelectChapter(chapter)),
            this.store.select(SelectionSelectors.selectDocument).subscribe(document => this.#handleSelectDocument(document)),
        );

        this.store.dispatch(LibraryActions.fetchChapters({bookUuid}));
        this.isMobile = breakpointObserver.isMatched(Breakpoints.XSmall);
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
        this.store.dispatch(SelectionActions.clearAll());
    }

    onCancel(): void {
        this.teacherOnlyActive = false;
        this.licenseUuidActive = false;
        this.dialogRef.close();
    }

    onSubmit(): void {
        this.sourceForm.validate();
        if (!this.sourceForm.valid) {
            return;
        }

        this.#setLoading(true);

        const selectedFile: SelectedFileInterface | undefined = this.selectedFile;

        if (undefined !== selectedFile) {
            this.store.dispatch(LibraryActions.addSourceFile({file: selectedFile.file}));
        } else {
            this.#addSource();
        }
    }

    removeFile(): void {
        this.selectedFile = undefined;
        this.sourceForm.setFile(null);
    }

    onFileChange(source?: ElementRef | AddSourceDialogForm): void {
        const file = source instanceof AddSourceDialogForm ? source?.file.value[0] : source?.nativeElement.files[0];
        const extension: string = file?.name.split('.').pop() ?? 'file';

        if (!file || !extension) {
            return;
        }

        this.selectedFile = {file, name: file.name, extension, type: this.extensionHelper.resolve(extension)};
        this.sourceForm.setFile(file);
    }

    #handleSelectChapter(chapter?: ChapterInterface): void {
        this.chapterUuid = chapter?.uuid;
        if (undefined === chapter) {
            return;
        }

        this.sourceForm.setChapterUuid(chapter.uuid);
    }

    #handleSelectDocument(document?: DocumentInterface): void {
        if (undefined === document) {
            return;
        }

        this.sourceForm.setDocumentDpsId(document.dpsId);
    }

    #handleAddSourceSuccess(): void {
        this.store.dispatch(LibraryActions.fetchSources({
            bookUuid: this.dialogData.bookUuid,
            chapterUuids: this.dialogData.chapterUuid,
            offset: 0,
            limit: 15,
        }));
        this.dialogRef.close();
    }

    #handleSourceFileSuccess(file: FileInterface): void {
        const selectedFile: SelectedFileInterface = this.selectedFile as SelectedFileInterface;

        this.selectedFile = {...selectedFile, id: file.id};
        this.#addSource();
    }

    #addSource(): void {
        const teacherOnly: boolean = this.sourceForm.visibleFor.value === 'teacher';
        const chapterUuid = this.sourceForm.chapterUuid.value;
        const documentDpsId = this.sourceForm.documentDpsId.value;

        this.store.dispatch(LibraryActions.addSource({
            bookUuid: this.bookUuid,
            fileId: this.selectedFile?.id,
            link: this.sourceForm.link.value,
            title: this.sourceForm.title.value,
            teacherOnly,
            chapterUuid,
            documentDpsId,
        }));
    }

    #setLoading(value: boolean): void {
        this.isLoading = value;

        if (value) {
            this.dialogRef.addPanelClass('mat-dialog-container--loading');
        } else {
            this.dialogRef.removePanelClass('mat-dialog-container--loading');
        }
    }
}
